import { useState, ChangeEvent, useEffect } from "react";
import Edit from "../../assets/icons/Edit";
import Delete from "../../assets/icons/Delete";
import DefaultButton from "../default-button/DefaultButton";
import AttendanceStudentsTable from "./AttendanceStudentsTable";
import UploadFile from "../../assets/icons/UploadFile";
import { Media } from "../../types";
import { FileUploadWithPreview } from "../FileUploadWithPreview";
import { showSnackbar } from "../../misc/utils";
import Cross from "../../assets/icons/Cross";
import { FilePDF } from "../../assets/icons/FilePDF";
import Eye from "../../assets/icons/Eye";
import Tick from "../../assets/icons/Tick";
import Plus from "../../assets/icons/Plus";

interface AttendanceCourseDetailsProps {
    selectedLessonId?: number;
    description?: string;
    onEditDescription?: (description: string) => void;
    onUploadTest?: () => void;
    onEditTest?: () => void;
    onDeleteTest?: () => void;
    onUploadHomework?: () => void;
    onEditHomework?: () => void;
    onDeleteHomework?: () => void;
    onUploadAdditionalMaterial?: () => void;
    onEditAdditionalMaterial?: () => void;
    onDeleteAdditionalMaterial?: () => void;
    onEditAttendance?: () => void;
}

type FileItem = Media | File;

interface FileMaterials {
    test: FileItem[]; // файлы теста
    homework: FileItem[]; // файлы домашнего задания
    additional: FileItem[]; // файлы доп. материала
}

type MaterialType = 'test' | 'homework' | 'additional';

export default function AttendanceCourseDetails({
    selectedLessonId,
    description = '',
    onEditDescription,
    onUploadTest,
    onEditTest,
    onDeleteTest,
    onUploadHomework,
    onEditHomework,
    onDeleteHomework,
    onUploadAdditionalMaterial,
    onEditAdditionalMaterial,
    onDeleteAdditionalMaterial,
    onEditAttendance
}: AttendanceCourseDetailsProps) {
    const [isEditingDescription, setIsEditingDescription] = useState(false);
    const [editedDescription, setEditedDescription] = useState(description);
    
    // TODO: Загружать файлы с сервера при изменении selectedLessonId
    // Используйте lessonsApi.getById(selectedLessonId) для получения данных урока
    // и извлеките файлы из ответа (например, lesson.test_media, lesson.homework_media, lesson.additional_media)
    const [fileMaterials, setFileMaterials] = useState<FileMaterials>({
        test: [],
        homework: [],
        additional: []
    });
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [currentMaterialType, setCurrentMaterialType] = useState<MaterialType | null>(null);
    const [newFiles, setNewFiles] = useState<Record<string, File | null>>({});

    // Обновляем editedDescription при изменении description или selectedLessonId
    useEffect(() => {
        if (!isEditingDescription) {
            setEditedDescription(description);
        }
    }, [description, selectedLessonId]);

    const handleStartEditDescription = () => {
        setIsEditingDescription(true);
        setEditedDescription(description);
    };

    const handleSaveDescription = () => {
        onEditDescription?.(editedDescription);
        setIsEditingDescription(false);
        showSnackbar('Описание успешно сохранено', 'success');
    };

    const handleCancelEditDescription = () => {
        setEditedDescription(description);
        setIsEditingDescription(false);
    };

    // Получить название типа материала
    const getMaterialTypeName = (type: MaterialType): string => {
        switch (type) {
            case 'test': return 'Тест';
            case 'homework': return 'Домашнее задание';
            case 'additional': return 'Доп. материал';
            default: return '';
        }
    };

    // Получить media_key для API (без суффикса _media_id, он добавляется в API)
    const getMediaKey = (type: MaterialType): string => {
        switch (type) {
            case 'test': return 'test';
            case 'homework': return 'homework';
            case 'additional': return 'additional';
            default: return '';
        }
    };

    // Получить полный media_key для API (с суффиксом _media_id)
    const getFullMediaKey = (type: MaterialType): string => {
        return `${getMediaKey(type)}_media_id`;
    };

    // Открыть модальное окно для управления файлами
    const openFileModal = (type: MaterialType) => {
        setCurrentMaterialType(type);
        setNewFiles({});
        setIsModalOpen(true);
    };

    // Закрыть модальное окно
    const closeFileModal = () => {
        setNewFiles({}); // Очищаем новые файлы при закрытии
        setIsModalOpen(false);
        setCurrentMaterialType(null);
    };

    // Добавить новый файл для загрузки
    const addNewFile = () => {
        const now = new Date().getTime().toString();
        setNewFiles(prev => ({ ...prev, [now]: null }));
    };

    // Обновить файл
    const updateNewFile = (key: string, file: File | null) => {
        setNewFiles(prev => ({ ...prev, [key]: file }));
    };

    // Удалить новый файл (еще не загруженный)
    const removeNewFile = (key: string) => {
        setNewFiles(prev => {
            const updated = { ...prev };
            delete updated[key];
            return updated;
        });
    };

    // Обработчик изменения файла
    const handleFileChange = (key: string) => (e: ChangeEvent<HTMLInputElement>) => {
        if (e.target.files?.length) {
            updateNewFile(key, e.target.files[0]);
        }
    };

    // Обработчик удаления существующего файла (симуляция без API)
    const handleDeleteFile = (type: MaterialType, fileIndex: number) => {
        // Симуляция удаления - просто удаляем из состояния
        setFileMaterials(prev => ({
            ...prev,
            [type]: prev[type].filter((_, index) => index !== fileIndex)
        }));
        showSnackbar('Файл успешно удален', 'success');
        
        // Вызываем колбэки если они есть
        switch (type) {
            case 'test':
                onDeleteTest?.();
                break;
            case 'homework':
                onDeleteHomework?.();
                break;
            case 'additional':
                onDeleteAdditionalMaterial?.();
                break;
        }
    };

    // Сохранить новые файлы (симуляция без API)
    const handleSaveFiles = () => {
        if (!selectedLessonId || !currentMaterialType) return;

        const filesToUpload = Object.entries(newFiles)
            .filter(([_, file]) => file !== null)
            .map(([_, file]) => file as File);

        if (filesToUpload.length === 0) {
            showSnackbar('Выберите файлы для загрузки', 'error');
            return;
        }

        // Сохраняем тип материала в локальную переменную, так как currentMaterialType может измениться
        const materialType = currentMaterialType;

        // Симуляция загрузки - добавляем файлы в состояние
        // В реальном приложении здесь будет API вызов, который вернет Media объекты
        setFileMaterials(prev => {
            const currentFiles = prev[materialType] || [];
            const updated = {
                ...prev,
                [materialType]: [...currentFiles, ...filesToUpload]
            };
            return updated;
        });

        showSnackbar(`Файлы успешно загружены (${filesToUpload.length})`, 'success');
        
        // Вызываем колбэки если они есть
        switch (materialType) {
            case 'test':
                onUploadTest?.();
                break;
            case 'homework':
                onUploadHomework?.();
                break;
            case 'additional':
                onUploadAdditionalMaterial?.();
                break;
        }
        
        // Очищаем новые файлы и закрываем модальное окно
        setNewFiles({});
        setIsModalOpen(false);
        setCurrentMaterialType(null);
    };

    // Загрузить файлы (открыть модальное окно)
    const handleUploadFiles = (type: MaterialType) => {
        openFileModal(type);
    };

    const renderFileSection = (
        type: MaterialType,
        files: FileItem[]
    ) => {
        const fileCount = files.length;
        
        if (fileCount > 0) {
            return (
                <div className="flex items-center gap-1 sm:gap-2 flex-wrap">
                    <div 
                        onClick={() => openFileModal(type)}
                        className="font-semibold text-xs sm:text-[13px] text-valley-main cursor-pointer hover:underline break-words"
                    >
                        {fileCount} {fileCount === 1 ? 'файл' : fileCount < 5 ? 'файла' : 'файлов'}
                    </div>
                    <div 
                        onClick={() => openFileModal(type)}
                        className="flex text-valley-gray cursor-pointer items-center justify-center p-[2px] border-none rounded-md bg-valley-blue/20 hover:bg-valley-blue/30 flex-shrink-0"
                        title="Редактировать"
                    >
                        <Edit/>
                    </div>
                </div>
            );
        } else {
            return (
                <DefaultButton 
                    Icon={UploadFile} 
                    buttonText="Загрузить" 
                    onClick={() => handleUploadFiles(type)}
                    additionalStyle="gap-1 items-center border border-valley-main text-valley-main py-1 px-2 sm:px-3 rounded-lg font-semibold text-xs sm:text-[13px] leading-[120%] w-full sm:w-auto" 
                />
            );
        }
    };

    if (!selectedLessonId) {
        return (
            <div className="flex flex-col h-full bg-white rounded-lg p-3 sm:p-4">
                <div className="flex items-center justify-center h-full text-gray-400 text-sm sm:text-base px-4 text-center">
                    Выберите урок для просмотра деталей
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-col h-full bg-white rounded-lg p-3 sm:p-4 md:p-6 gap-4 sm:gap-6 overflow-y-auto min-h-0">
            <div className="flex flex-col lg:flex-row items-start justify-between gap-4 sm:gap-6 flex-shrink-0">
                {/* Описание урока */}
                <div className="w-full lg:w-1/2 flex-shrink-0">
                    <div className="flex items-center gap-2 font-medium text-sm sm:text-[15px] leading-[19.5px] text-valley-black mb-2">
                        Описание
                        {!isEditingDescription && (
                            <div 
                                className="flex cursor-pointer items-center justify-center p-[2px] border-none rounded-md bg-blue/20 hover:bg-blue/30"
                                onClick={handleStartEditDescription}
                                title={description ? "Редактировать описание" : "Добавить описание"}
                            >
                                <Edit/>
                            </div>
                        )}
                    </div>
                    {/* Фиксированная высота для предотвращения прыжков верстки */}
                    <div className="min-h-[120px] sm:min-h-[180px] flex flex-col">
                        {isEditingDescription ? (
                            <div className="flex flex-col gap-2">
                                <textarea
                                    value={editedDescription}
                                    onChange={(e) => setEditedDescription(e.target.value)}
                                    className="bg-[#F0F3FA] w-full resize-none outline-none rounded-lg px-3 sm:px-4 py-2 sm:py-3 h-24 sm:h-32 border border-gray-200 text-sm sm:text-base"
                                    placeholder="Введите описание урока"
                                />
                                <div className="flex flex-col sm:flex-row gap-2">
                                    <DefaultButton
                                        buttonText="Сохранить"
                                        Icon={Tick}
                                        onClick={handleSaveDescription}
                                        additionalStyle="text-white bg-valley-dark-green px-3 sm:px-4 py-2 hover:bg-valley-dark-green/90 text-sm sm:text-base w-full sm:w-auto"
                                    />
                                    <DefaultButton
                                        buttonText="Отмена"
                                        onClick={handleCancelEditDescription}
                                        additionalStyle="text-valley-gray border border-valley-gray px-3 sm:px-4 py-2 hover:bg-valley-gray/10 text-sm sm:text-base w-full sm:w-auto"
                                    />
                                </div>
                            </div>
                        ) : (
                            <div className="text-valley-black min-h-[32px] py-2 text-sm sm:text-base">
                                {description ? (
                                    <div className="break-words">
                                        {description}
                                    </div>
                                ) : (
                                    <div className="bg-valley-main/10 py-3 px-3 sm:px-4 md:px-8 rounded-lg flex items-center justify-center w-full">
                                        <DefaultButton
                                            buttonText="Добавить описание"
                                            Icon={Plus}
                                            onClick={handleStartEditDescription}
                                            additionalStyle="text-white border border-valley-main px-3 sm:px-4 py-1 sm:py-2 gap-1 items-center !text-valley-main rounded-lg font-semibold text-xs sm:text-[13px] leading-[120%]"
                                        />
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                <div className="flex flex-col gap-3 sm:gap-4 w-full lg:w-1/2 flex-shrink-0">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-0">
                        <div className="text-sm sm:text-base font-medium text-valley-black">
                            Тест
                        </div>
                        <div>
                            {renderFileSection('test', fileMaterials.test)}
                        </div>
                    </div>

                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-0">
                        <div className="text-sm sm:text-base font-medium text-valley-black">
                            Домашнее задание
                        </div>
                        <div>
                            {renderFileSection('homework', fileMaterials.homework)}
                        </div>
                    </div>

                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-0">
                        <div className="text-sm sm:text-base font-medium text-valley-black">
                           Доп. материал
                        </div>
                        <div>
                            {renderFileSection('additional', fileMaterials.additional)}
                        </div>
                    </div>

                </div>


            </div>
           

            {/* Таблица студентов */}
            <div className="flex flex-col gap-4 flex-1">
                <AttendanceStudentsTable 
                    selectedLessonId={selectedLessonId}
                    onEditAttendance={onEditAttendance} 
                />
            </div>

            {/* Модальное окно для управления файлами */}
            {isModalOpen && currentMaterialType && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-3 sm:p-4 md:p-6">
                    <div className="bg-white rounded-lg p-4 sm:p-5 md:p-6 max-w-2xl w-full max-h-[90vh] sm:max-h-[80vh] overflow-y-auto">
                        <div className="flex items-center justify-between mb-4 sm:mb-6 gap-2">
                            <h2 className="text-lg sm:text-xl font-semibold text-valley-black break-words flex-1">
                                Управление файлами: {getMaterialTypeName(currentMaterialType)}
                            </h2>
                            <button
                                onClick={closeFileModal}
                                className="text-valley-gray hover:text-valley-black flex-shrink-0"
                            >
                                <Cross additionalStyle="w-5 h-5 sm:w-6 sm:h-6" />
                            </button>
                        </div>

                        {/* Список существующих файлов */}
                        {fileMaterials[currentMaterialType].length > 0 && (
                            <div className="mb-4 sm:mb-6">
                                <h3 className="text-xs sm:text-sm font-medium text-valley-gray mb-2">
                                    Загруженные файлы
                                </h3>
                                <div className="flex flex-col gap-2">
                                    {fileMaterials[currentMaterialType].map((file, index) => {
                                        // Создаем уникальный ключ для каждого файла
                                        const fileKey = 'id' in file && file.id 
                                            ? `media-${file.id}` 
                                            : 'name' in file && file.name 
                                                ? `file-${file.name}-${index}` 
                                                : `file-${index}`;
                                        return (
                                        <div key={fileKey} className="flex items-center justify-between p-2 sm:p-3 border border-valley-light-gray rounded-lg gap-2">
                                            <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
                                                <FilePDF />
                                                <div className="flex flex-col flex-1 min-w-0">
                                                    <span className="text-xs sm:text-sm font-medium text-valley-black truncate">
                                                        {'name' in file ? file.name : file.filename || `Файл ${index + 1}`}
                                                    </span>
                                                    {'createdAt' in file && (
                                                        <span className="text-xs text-valley-gray">
                                                            {new Date(file.createdAt).toLocaleDateString()}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
                                                {'link' in file && file.link && (
                                                    <a
                                                        href={file.link}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="p-1.5 sm:p-2 bg-valley-background-blue rounded-md hover:bg-valley-blue/20"
                                                        title="Просмотреть"
                                                    >
                                                        <Eye additionalStyle="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                                                    </a>
                                                )}
                                                <button
                                                    onClick={() => handleDeleteFile(currentMaterialType, index)}
                                                    className="p-1.5 sm:p-2 bg-valley-red/20 rounded-md hover:bg-valley-red/30"
                                                    title="Удалить"
                                                >
                                                    <Delete additionalStyle="w-3.5 h-3.5 sm:w-4 sm:h-4 text-valley-red" />
                                                </button>
                                            </div>
                                        </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        {/* Новые файлы для загрузки */}
                        <div className="mb-4 sm:mb-6">
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-2 gap-2">
                                <h3 className="text-xs sm:text-sm font-medium text-valley-gray">
                                    Новые файлы
                                </h3>
                                <button
                                    onClick={addNewFile}
                                    className="flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-1 border border-valley-main text-valley-main rounded-lg hover:bg-valley-main/10 text-xs sm:text-sm w-full sm:w-auto justify-center"
                                >
                                    <UploadFile color="currentColor" />
                                    <span className="font-semibold">Добавить файл</span>
                                </button>
                            </div>
                            <div className="flex flex-col gap-2">
                                {Object.entries(newFiles).map(([key, file]) => (
                                    <div key={key} className="flex items-center gap-2">
                                        <div className="flex-1 min-w-0">
                                            <FileUploadWithPreview
                                                wrapperClassName="flex-none"
                                                mode="edit"
                                                value={file}
                                                updateValueRaw={(value) => updateNewFile(key, value)}
                                                handleChange={handleFileChange(key)}
                                                buttonKeys={file ? ['view', 'download', 'delete'] : []}
                                            />
                                        </div>
                                        {file && (
                                            <button
                                                onClick={() => removeNewFile(key)}
                                                className="p-1.5 sm:p-2 text-valley-red hover:bg-valley-red/20 rounded-md flex-shrink-0"
                                                title="Удалить"
                                            >
                                                <Cross additionalStyle="w-4 h-4 sm:w-5 sm:h-5 text-valley-red" />
                                            </button>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Кнопки действий */}
                        <div className="flex flex-col sm:flex-row items-stretch sm:items-center sm:justify-end gap-2 sm:gap-3">
                            <DefaultButton
                                buttonText="Отмена"
                                onClick={closeFileModal}
                                additionalStyle="px-3 sm:px-4 py-2 border border-valley-gray text-valley-gray hover:bg-valley-gray/10 text-sm sm:text-base w-full sm:w-auto"
                            />
                            <DefaultButton
                                buttonText="Сохранить"
                                onClick={handleSaveFiles}
                                additionalStyle="px-3 sm:px-4 py-2 bg-valley-dark-green text-white hover:bg-valley-dark-green/90 text-sm sm:text-base w-full sm:w-auto"
                            />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

